import { Ban, CircleCheck, Clock, TriangleAlert } from 'lucide-react'
import { type ReactNode } from 'react'

import type { FinanceEntrySummary } from '@aeris/contracts'
import {
  FINANCE_STATUS_TEXT,
  overdueText,
  type FinanceStatusText,
} from '@aeris/status-meta'
import { type StatusTone } from '@aeris/ui'

/**
 * O que cada situação de um título QUER DIZER — F156.
 *
 * Este módulo é o que o Financeiro **não tinha**: a cor de cada situação era
 * calculada à mão dentro de `routes/financeiro.tsx`, solta, sem nome e sem
 * ninguém decidindo nada. Foi assim que "Cancelado" acabou cinza aqui e
 * vermelho em Vendas e Compras — o mesmo ato, três telas, duas cores.
 *
 * **Só constantes, nenhum componente** — Fast Refresh.
 */

type Status = FinanceEntrySummary['status']

export type FinanceStatusMeta = Omit<FinanceStatusText, 'tone'> & {
  readonly tone: StatusTone
  readonly icon: ReactNode
}

const icon = (Glyph: typeof Clock): ReactNode => (
  <Glyph aria-hidden="true" className="h-3 w-3" />
)

const ICONS: Record<Status, ReactNode> = {
  open: icon(Clock),
  settled: icon(CircleCheck),
  cancelled: icon(Ban),
}

// O texto mora em `@aeris/status-meta` (F305); aqui só entra o ícone.
export const FINANCE_STATUS_META: Readonly<Record<Status, FinanceStatusMeta>> =
  {
    open: { ...FINANCE_STATUS_TEXT.open, icon: ICONS.open },
    settled: { ...FINANCE_STATUS_TEXT.settled, icon: ICONS.settled },
    cancelled: { ...FINANCE_STATUS_TEXT.cancelled, icon: ICONS.cancelled },
  }

export const overdueMeta = (daysOverdue: number): FinanceStatusMeta => ({
  ...overdueText(daysOverdue),
  icon: icon(TriangleAlert),
})

export const FINANCE_LEGEND = [
  FINANCE_STATUS_META.open,
  overdueMeta(3),
  FINANCE_STATUS_META.settled,
  FINANCE_STATUS_META.cancelled,
] as const
